import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';

import { decorativeIcon, LoaderCircle } from '../../lib/icons';
import { cn } from '../../lib/cn';

/**
 * The proven primitive. Everything else in this package follows its shape:
 * `cva` for the variant matrix, semantic tokens for every colour, logical
 * properties only (so an RTL locale needs no override), and an accessible name
 * that never disappears.
 *
 * Five variants, and each of them means something:
 *
 * - `primary`   the single most important action on the screen. One per screen.
 * - `secondary` the alternative. White fill, a border that itself clears 3:1.
 * - `quiet`     DESIGN.md's "ghost": a link that happens to be a button.
 * - `danger`    destructive, and only inside a dialog that has already named
 *               what will be destroyed.
 * - `danger-secondary` destructive but NOT the primary — "Rejeter" standing
 *               beside "Résoudre". Four page mockups invented this
 *               independently, which is what a missing variant looks like.
 */
const button = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'font-sans text-center',
    'rounded-control border',
    'cursor-pointer select-none',
    'transition-colors dur-state ease-out',
    // A disabled control still has to be readable. `opacity-60` on a
    // half-legible label is how a "greyed out" state becomes unreadable at
    // 02:00; a real fill and a real ink token do not have that problem.
    'disabled:cursor-not-allowed',
    'disabled:border-transparent disabled:bg-surface-sunk disabled:text-ink-disabled',
    'disabled:shadow-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'border-transparent bg-action text-action-fg',
          'hover:bg-action-hover active:bg-action-press',
        ],
        secondary: [
          // #C6CDCC (`border-strong`) is 1.61:1 and fails WCAG 1.4.11 for a
          // control boundary. Interactive borders are always `border-field`.
          'border-border-field bg-surface text-ink',
          'hover:bg-surface-sunk active:bg-surface-sunk',
        ],
        quiet: [
          'border-transparent bg-transparent text-link',
          'hover:underline hover:underline-offset-4 active:underline',
        ],
        danger: [
          'border-transparent bg-danger-action text-danger-action-fg',
          'hover:bg-danger-action-hover active:bg-danger-action-press',
        ],
        'danger-secondary': [
          'border-status-danger-ink bg-transparent text-status-danger-ink',
          'hover:bg-status-danger-surface active:bg-status-danger-surface',
        ],
      },
      size: {
        /** Desktop density. 32 px is the pointer-target floor, not the touch one. */
        sm: 'min-h-8 px-3 text-body-compact font-bold',
        /** The admin default: 40 px, `0 18px`, Sans 700 13 px. */
        md: 'min-h-control px-[18px] text-body-compact font-bold',
        /** The worker floor: 48 px, one-handed, in gloves, in poor light. */
        lg: 'min-h-touch px-5 text-subject font-bold',
        /**
         * The punch key. 112 px tall, full width, headline label, and — on
         * `primary` — the inset bevel. Never IN and OUT at once.
         */
        xl: 'min-h-key w-full flex-col gap-1 px-6 text-headline',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    compoundVariants: [
      // Every primary carries the bevel; only the punch key carries it at 5 px.
      {
        variant: 'primary',
        size: ['sm', 'md', 'lg'],
        class: 'shadow-key-sm active:translate-y-px active:shadow-key-sm-pressed',
      },
      {
        variant: 'primary',
        size: 'xl',
        // The bevel is INSIDE the box, so the drawn key face and the tap target
        // are one rectangle. On press it drops to 2 px and the label follows.
        class: 'shadow-key active:translate-y-0.5 active:shadow-key-pressed',
      },
    ],
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  },
);

export type ButtonVariants = VariantProps<typeof button>;

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    ButtonVariants {
  /**
   * Shows a busy indicator and blocks activation without hiding the label.
   * Pass `loadingLabel` to name the request in progress ("Envoi…"); DESIGN.md
   * forbids a bare spinner and forbids a button that merely looks disabled
   * mid-request.
   */
  readonly loading?: boolean;
  /** Replaces the label while `loading`. Already translated by the caller. */
  readonly loadingLabel?: string;
  /**
   * The consequence, named. On the punch key this is the difference between
   * "Pointer la sortie" and "Pointer la sortie — termine le quart de ce soir".
   */
  readonly subLabel?: ReactNode;
  /** Rendered before the label; decorative, so hidden from assistive tech. */
  readonly iconStart?: ReactNode;
  /** Rendered after the label; decorative. */
  readonly iconEnd?: ReactNode;
  /**
   * Renders the single child element with this recipe instead of a `<button>`.
   *
   * A control that **changes route** must be an `<a>`: middle-click and "open
   * in a new tab" are navigation, not decoration, and a `<button onClick=
   * navigate>` takes both away. Three screens spelled the secondary recipe
   * out by hand against the same tokens for exactly this — a 48 px
   * *Voir le quart* link — and a hand-copied recipe is a recipe that drifts.
   *
   * ```tsx
   * <Button asChild variant="secondary" size="lg">
   *   <Link to={href}>{t('next.cta')}</Link>
   * </Button>
   * ```
   *
   * `loading` and `disabled` are **not** applied to the child: neither has a
   * meaning on an anchor, and `disabled` on an `<a>` is invisible to the
   * browser. A navigation that can be refused is a `<button>`.
   */
  readonly asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    fullWidth,
    loading = false,
    loadingLabel,
    subLabel,
    disabled = false,
    iconStart,
    iconEnd,
    children,
    asChild = false,
    // A button inside a form defaults to `submit` in HTML, which has surprised
    // enough people that the explicit default here is `button`.
    type = 'button',
    ...rest
  },
  ref,
) {
  const label = loading && loadingLabel !== undefined ? loadingLabel : children;

  if (asChild) {
    /*
     * `Slottable` has to sit at the top level for Radix to find it, so the
     * icons and the sub-label are siblings of the label rather than wrapped
     * in the inner span the `<button>` path uses. The recipe's own
     * `inline-flex items-center justify-center gap-2` already lays them out,
     * so the two renderings are the same drawing.
     */
    return (
      <Slot.Root
        {...rest}
        ref={ref as Ref<HTMLElement>}
        className={cn(button({ variant, size, fullWidth }), className)}
      >
        {iconStart ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {iconStart}
          </span>
        ) : null}
        <Slot.Slottable>{children}</Slot.Slottable>
        {iconEnd ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {iconEnd}
          </span>
        ) : null}
        {subLabel !== undefined && subLabel !== null && subLabel !== false ? (
          <span className="text-body-compact font-medium opacity-90">{subLabel}</span>
        ) : null}
      </Slot.Root>
    );
  }

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(button({ variant, size, fullWidth }), className)}
    >
      <span className="inline-flex items-center justify-center gap-2">
        {loading ? (
          <LoaderCircle
            {...decorativeIcon('sm')}
            data-testid="button-spinner"
            className="shrink-0 animate-spin motion-reduce:animate-none"
          />
        ) : iconStart ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {iconStart}
          </span>
        ) : null}
        <span>{label}</span>
        {iconEnd ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {iconEnd}
          </span>
        ) : null}
      </span>
      {subLabel !== undefined && subLabel !== null && subLabel !== false ? (
        <span className="text-body-compact font-medium opacity-90">{subLabel}</span>
      ) : null}
    </button>
  );
});
